import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const API_BASE = "https://panel.haymestasarim.com/api";
const API_ILETISIM = `${API_BASE}/iletisim/public`;

function detectLang() {
  const l = navigator.language?.slice(0, 2);
  return ["tr", "en", "de"].includes(l) ? l : "tr";
}

function resolveImage(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) return `https://panel.haymestasarim.com${url}`;
  return `https://panel.haymestasarim.com/${url}`;
}

function getText(obj, field, lang) {
  if (!obj) return "";
  const value = obj?.[`${field}_${lang}`];
  if (value && value.trim() !== "") {
    return value;
  }
  return "";
}

function getServiceText(obj, field, lang) {
  if (!obj) return "";

  return (
    obj?.[`${field}_${lang}`] ||
    obj?.[`${field}_tr`] ||
    obj?.[`${field}_en`] ||
    obj?.[`${field}_de`] ||
    ""
  );
}

function getFaqText(obj, field, lang) {
  if (!obj) return "";
  const value = obj?.[`${field}_${lang}`];
  if (value && value.trim() !== "") {
    return value;
  }
  return "";
}

function getServicePrice(service, lang) {
  if (!service) return "";

  return (
    service?.[`price_${lang}`] ||
    service?.price ||
    ""
  );
}

function getServiceCurrency(service, lang) {
  if (!service) return "";

  return (
    service?.[`currency_${lang}`] ||
    service?.currency ||
    "CHF"
  );
}

const TEXT = {
  tr: {
    welcome: "Hoş geldiniz",
    search: "Hizmet ara...",
    featured: "Öne Çıkan Hizmetler",
    categories: "Hizmet Kategorileri",
    faq: "Sıkça Sorulan Sorular",
    appointment: "Randevu Al",
    name: "Adınız Soyadınız",
    phone: "Telefon Numaranız",
    service: "Hizmet",
    date: "Tarih",
    time: "Saat",
    note: "Notunuz",
    send: "Randevu Talebi Gönder",
    success: "Randevu talebiniz alındı.",
    error: "Bir hata oluştu.",
    noData: "Henüz hizmet eklenmemiş.",
    duration: "Süre",
    price: "Fiyat",
    whatsapp: "WhatsApp ile İletişim",
    location: "Konuma Git",
    close: "Kapat",
    selectService: "Hizmet Seçiniz",
    minute: "dk",
    contact: "İletişim",
    follow: "Bizi Takip Edin",
    web: "Web Sitesi",
  },
  en: {
    welcome: "Welcome",
    search: "Search services...",
    featured: "Featured Services",
    categories: "Service Categories",
    faq: "Frequently Asked Questions",
    appointment: "Book Appointment",
    name: "Full Name",
    phone: "Phone Number",
    service: "Service",
    date: "Date",
    time: "Time",
    note: "Your Note",
    send: "Send Appointment Request",
    success: "Your appointment request has been received.",
    error: "An error occurred.",
    noData: "No services have been added yet.",
    duration: "Duration",
    price: "Price",
    whatsapp: "Contact via WhatsApp",
    location: "Get Directions",
    close: "Close",
    selectService: "Select Service",
    minute: "min",
    contact: "Contact",
    follow: "Follow Us",
    web: "Website",
  },
  de: {
    welcome: "Willkommen",
    search: "Dienstleistung suchen...",
    featured: "Empfohlene Dienstleistungen",
    categories: "Dienstleistungskategorien",
    faq: "Häufig gestellte Fragen",
    appointment: "Termin buchen",
    name: "Vor- und Nachname",
    phone: "Telefonnummer",
    service: "Dienstleistung",
    date: "Datum",
    time: "Uhrzeit",
    note: "Ihre Nachricht",
    send: "Terminanfrage senden",
    success: "Ihre Terminanfrage wurde erhalten.",
    error: "Ein Fehler ist aufgetreten.",
    noData: "Noch keine Dienstleistungen hinzugefügt.",
    duration: "Dauer",
    price: "Preis",
    whatsapp: "Kontakt über WhatsApp",
    location: "Route anzeigen",
    close: "Schließen",
    selectService: "Dienstleistung auswählen",
    minute: "Min.",
    contact: "Kontakt",
    follow: "Folgen Sie uns",
    web: "Webseite",
  },
};

export default function BeatyMenu() {
  const { kurumId } = useParams();

  const [lang, setLang] = useState(detectLang());
  const TT = TEXT[lang] || TEXT.tr;

  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState({});
  const [contact, setContact] = useState({});
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [toast, setToast] = useState("");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    date: "",
    time: "",
    note: "",
  });

  useEffect(() => {
    if (!kurumId) return;

    setLoading(true);

    Promise.all([
      fetch(`${API_BASE}/beaty/public/${kurumId}?lang=${lang}`).then((r) => r.json()),
      fetch(`${API_ILETISIM}?kurum_id=${kurumId}&lang=${lang}`)
        .then((r) => r.json())
        .catch(() => ({ data: {} })),
    ])
      .then(([beatyRes, contactRes]) => {
        const data = beatyRes?.data || {};
        setBusiness(data.business || {});
        setContact(contactRes?.data || {});
        setCategories(data.categories || []);
        setServices(data.services || []);
        setFaqs(data.faqs || []);
      })
      .catch((err) => {
        console.error("Beaty public load error:", err);
      })
      .finally(() => setLoading(false));
  }, [kurumId, lang]);

const featuredServices = useMemo(() => {
  return services.filter((s) => {
    const title = getServiceText(s, "title", lang);
    return (s.is_featured === true || s.is_featured === 1) && title.trim() !== "";
  });
}, [services, lang]);

const filteredServices = useMemo(() => {
  const visibleServices = services.filter((s) => {
    const title = getServiceText(s, "title", lang);
    return title && title.trim() !== "";
  });

  const q = search.toLowerCase().trim();
  if (!q) return visibleServices;

  return visibleServices.filter((s) => {
    const title = getServiceText(s, "title", lang).toLowerCase();
    const desc = getServiceText(s, "description", lang).toLowerCase();
    return title.includes(q) || desc.includes(q);
  });
}, [services, search, lang]);

const groupedServices = useMemo(() => {
  return categories
    .filter((cat) => {
      const catTitle = getServiceText(cat, "title", lang);
      return catTitle && catTitle.trim() !== "";
    })
    .map((cat) => ({
      ...cat,
      services: filteredServices.filter(
        (s) => Number(s.category_id) === Number(cat.id)
      ),
    }))
    .filter((cat) => cat.services.length > 0);
}, [categories, filteredServices, lang]);


  const openAppointment = (service) => {
    setSelectedService(service);
    setAppointmentOpen(true);
  };

  const sendAppointment = async () => {
    if (!form.name.trim() || !form.phone.trim() || !selectedService) {
      alert(`${TT.name} / ${TT.phone} / ${TT.service}`);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/beaty/appointments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          kurum_id: kurumId,
          service_id: selectedService.id,
          customer_name: form.name,
          customer_phone: form.phone,
          appointment_date: form.date || null,
          appointment_time: form.time || null,
          note: form.note || "",
          status: "new",
        }),
      });

      if (!res.ok) throw new Error("Appointment failed");

      setToast(TT.success);
      setForm({
        name: "",
        phone: "",
        date: "",
        time: "",
        note: "",
      });
      setAppointmentOpen(false);
      setSelectedService(null);

      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      console.error(err);
      alert(TT.error);
    }
  };

  const businessName =
    contact.kurum_adi ||
    getText(business, "firma_adi", lang) ||
    business.kurum_adi ||
    "Güzellik Salonu";

  const businessLogo =
    contact.logo_url || contact.logo || business.logo_url || business.logo || "";

  const phone = contact.telefon || contact.phone || business.telefon || "";
  const email = contact.email || business.email || "";
  const address =
    contact.adres ||
    contact.adres_tr ||
    contact.address ||
    business.adres ||
    business.adres_tr ||
    business.address ||
    "";

  const website = contact.website || business.website || "";
  const instagram = contact.instagram || business.instagram || "";
  const facebook = contact.facebook || business.facebook || "";
  const youtube = contact.youtube || business.youtube || "";
  const mapUrl = contact.harita_url || contact.map_url || business.harita_url || "";

const googleReviewUrl = business.google_review_url || "";

const googleReviewTitle =
  business[`google_review_title_${lang}`] ||
  business.google_review_title_tr ||
  "";

const googleReviewDesc =
  business[`google_review_desc_${lang}`] ||
  business.google_review_desc_tr ||
  "";

const showGoogleReview =
  business.google_review_active === true &&
  googleReviewUrl.trim() !== "";
  const whatsappNumber = contact.whatsapp || business.whatsapp || phone || "";
  const whatsappLink = whatsappNumber
    ? `https://wa.me/${String(whatsappNumber).replace(/\D/g, "")}`
    : "";

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <div>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerOverlay}></div>

        <div style={styles.langBox}>
          {["tr", "en", "de"].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              style={{
                ...styles.langBtn,
                ...(lang === l ? styles.langBtnActive : {}),
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <div style={styles.logoWrap}>
          {businessLogo ? (
            <img
              src={resolveImage(businessLogo)}
              alt=""
              style={styles.logo}
            />
          ) : (
            <div style={styles.logoPlaceholder}>💇‍♀️</div>
          )}
        </div>

        <h1 style={styles.title}>{businessName}</h1>

        <p style={styles.subtitle}>{TT.welcome}</p>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={TT.search}
          style={styles.search}
        />
            </header>

      <main style={styles.main}>


        {featuredServices.length > 0 && (
          <section style={styles.section}>
            <SectionTitle title={`⭐ ${TT.featured}`} />

            <div style={styles.horizontal}>
              {featuredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  lang={lang}
                  TT={TT}
                  onAppointment={() => openAppointment(service)}
                />
              ))}
            </div>
          </section>
        )}

{groupedServices.length > 0 && (
<section style={styles.section}>
  <SectionTitle title={TT.categories} />

  {services.length === 0 ? (

            <div style={styles.empty}>{TT.noData}</div>
          ) : (
            groupedServices.map((cat) => (
              <div key={cat.id} style={styles.categoryBlock}>
                <h3 style={styles.categoryTitle}>
{getServiceText(cat, "title", lang)}
                </h3>

                <div style={styles.grid}>
                  {cat.services.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      lang={lang}
                      TT={TT}
                      onAppointment={() => openAppointment(service)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
</section>
)}

  {faqs.filter((faq) => {
  const q = getFaqText(faq, "question", lang);
  const a = getFaqText(faq, "answer", lang);
  return q && q.trim() !== "" && a && a.trim() !== "";
}).length > 0 && (
  <section style={styles.section}>
    <SectionTitle title={TT.faq} />

    <div style={styles.faqBox}>
      {faqs
        .filter((faq) => {
          const q = getFaqText(faq, "question", lang);
          const a = getFaqText(faq, "answer", lang);
          return q && q.trim() !== "" && a && a.trim() !== "";
        })
        .map((faq) => (
          <details key={faq.id} style={styles.faqItem}>
            <summary style={styles.faqQuestion}>
              {getFaqText(faq, "question", lang)}
            </summary>
            <p style={styles.faqAnswer}>
              {getFaqText(faq, "answer", lang)}
            </p>
          </details>
        ))}
    </div>
  </section>
)}


        {showGoogleReview && (
          <section style={styles.reviewBox}>
            <div style={styles.reviewIcon}>⭐</div>

            <h2 style={styles.reviewTitle}>
              {googleReviewTitle}
            </h2>

            <p style={styles.reviewText}>
              {googleReviewDesc}
            </p>

            <a
              href={googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              style={styles.reviewBtn}
            >
              {lang === "de"
                ? "Auf Google bewerten"
                : lang === "en"
                ? "Review on Google"
                : "Google’da Yorum Yap"}
            </a>
          </section>
        )}

      </main>

      <footer style={styles.footer}>

        <div style={styles.footerGrid}>
          <div>
            <h3 style={styles.footerTitle}>{businessName}</h3>
            <p style={styles.footerText}>
              {getText(business, "slogan", lang) ||
                contact.aciklama ||
                contact.description ||
                "Kuaför, güzellik salonu, bakım hizmetleri ve randevu talepleri."}
            </p>
          </div>

          <div>
            <h3 style={styles.footerTitle}>{TT.contact}</h3>

            {address && (
              <p style={styles.footerLine}>
                <span>📍</span>
                <span>{address}</span>
              </p>
            )}

            {phone && (
              <p style={styles.footerLine}>
                <span>📞</span>
                <a href={`tel:${phone}`} style={styles.footerLink}>
                  {phone}
                </a>
              </p>
            )}

            {email && (
              <p style={styles.footerLine}>
                <span>📧</span>
                <a href={`mailto:${email}`} style={styles.footerLink}>
                  {email}
                </a>
              </p>
            )}

            {website && (
              <p style={styles.footerLine}>
                <span>🌐</span>
                <a href={website} target="_blank" rel="noreferrer" style={styles.footerLink}>
                  {website}
                </a>
              </p>
            )}
          </div>

          <div>
            <h3 style={styles.footerTitle}>{TT.follow}</h3>

            <div style={styles.socialLinks}>
              {instagram && (
                <a style={styles.socialBtn} href={instagram} target="_blank" rel="noreferrer">
                  Instagram
                </a>
              )}

              {facebook && (
                <a style={styles.socialBtn} href={facebook} target="_blank" rel="noreferrer">
                  Facebook
                </a>
              )}

              {youtube && (
                <a style={styles.socialBtn} href={youtube} target="_blank" rel="noreferrer">
                  YouTube
                </a>
              )}

              {whatsappLink && (
                <a style={styles.whatsappBtn} href={whatsappLink} target="_blank" rel="noreferrer">
                  WhatsApp
                </a>
              )}

              {mapUrl && (
                <a style={styles.mapBtn} href={mapUrl} target="_blank" rel="noreferrer">
                  {TT.location}
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>

      {appointmentOpen && (
        <div style={styles.modalOverlay} onClick={() => setAppointmentOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeBtn} onClick={() => setAppointmentOpen(false)}>
              ×
            </button>

            <h2 style={styles.modalTitle}>{TT.appointment}</h2>

            {selectedService && (
              <div style={styles.selectedService}>
<strong>{getServiceText(selectedService, "title", lang)}</strong>                <span>
                  {selectedService.price} {selectedService.currency || "CHF"}
                </span>
              </div>
            )}

            <input
              style={styles.input}
              placeholder={TT.name}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <input
              style={styles.input}
              placeholder={TT.phone}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />

            <div style={styles.row}>
              <input
                style={styles.input}
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />

              <input
                style={styles.input}
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
              />
            </div>

            <textarea
              style={{ ...styles.input, minHeight: 90 }}
              placeholder={TT.note}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />

            <button style={styles.sendBtn} onClick={sendAppointment}>
              {TT.send}
            </button>
          </div>
        </div>
      )}

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <div style={styles.sectionTitleWrap}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <div style={styles.sectionLine}></div>
    </div>
  );
}

function ServiceCard({ service, lang, TT, onAppointment }) {
  const title = getServiceText(service, "title", lang);
  const desc = getServiceText(service, "description", lang);

  return (
    <article style={styles.card}>
      <div style={styles.imageBox}>
        {service.image || service.image_url ? (
          <img
            src={resolveImage(service.image || service.image_url)}
            alt={title}
            style={styles.image}
          />
        ) : (
          <div style={styles.imageEmpty}>✨</div>
        )}
      </div>

      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle}>{title}</h3>

        {desc && <p style={styles.cardDesc}>{desc}</p>}

        <div style={styles.meta}>
          <span>
            {TT.price}: <b>{getServicePrice(service, lang)} {getServiceCurrency(service, lang)}</b>
          </span>

          {service.duration && (
            <span>
              {TT.duration}: <b>{service.duration} {TT.minute}</b>
            </span>
          )}
        </div>

        <button style={styles.appointmentBtn} onClick={onAppointment}>
          {TT.appointment}
        </button>
      </div>
    </article>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#fff7ed",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    color: "#1f2937",
  },
  loading: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#f97316,#1e40af)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 16,
    fontWeight: 700,
  },
  spinner: {
    width: 46,
    height: 46,
    border: "4px solid rgba(255,255,255,.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  header: {
    position: "relative",
    padding: "28px 18px 78px",
    textAlign: "center",
    color: "#fff",
    background: "linear-gradient(135deg,#f97316 0%,#2563eb 55%,#1e3a8a 100%)",
    overflow: "hidden",
  },
  headerOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at top left, rgba(255,255,255,.22), transparent 35%)",
  },
  langBox: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    justifyContent: "center",
    gap: 8,
    marginBottom: 22,
  },
  langBtn: {
    padding: "7px 14px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.45)",
    background: "transparent",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  langBtnActive: {
    background: "#fff",
    color: "#1e3a8a",
  },
  logoWrap: {
    position: "relative",
    zIndex: 2,
    width: 96,
    height: 96,
    margin: "0 auto 14px",
    borderRadius: "50%",
    background: "rgba(255,255,255,.22)",
    padding: 4,
  },
  logo: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
    background: "#fff",
  },
  logoPlaceholder: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 42,
    background: "#fff",
    color: "#f97316",
  },
  title: {
    position: "relative",
    zIndex: 2,
    margin: 0,
    fontSize: "clamp(28px, 7vw, 44px)",
    fontWeight: 900,
  },
  subtitle: {
    position: "relative",
    zIndex: 2,
    marginTop: 8,
    opacity: 0.95,
    fontSize: 17,
  },
  search: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 520,
    marginTop: 24,
    padding: "16px 18px",
    borderRadius: 18,
    border: "none",
    fontSize: 16,
    fontWeight: 700,
    color: "#1f2937",
    boxShadow: "0 12px 28px rgba(0,0,0,.22)",
  },
  main: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "46px 16px 34px",
    position: "relative",
    zIndex: 3,
  },
  section: {
    marginBottom: 42,
  },
  sectionTitleWrap: {
    textAlign: "center",
    margin: "10px 0 24px",
  },
  sectionTitle: {
    fontSize: "clamp(22px, 5vw, 30px)",
    fontWeight: 900,
    color: "#1e3a8a",
    margin: 0,
    lineHeight: 1.25,
  },
  sectionLine: {
    width: 76,
    height: 4,
    background: "linear-gradient(90deg,#f97316,#2563eb)",
    borderRadius: 8,
    margin: "12px auto 0",
  },
  horizontal: {
    display: "flex",
    gap: 18,
    overflowX: "auto",
    padding: "8px 2px 20px",
    WebkitOverflowScrolling: "touch",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 18,
  },
  categoryBlock: {
    marginBottom: 32,
  },
  categoryTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: "#111827",
    margin: "10px 0 16px",
    lineHeight: 1.3,
  },
  card: {
    minWidth: 280,
    background: "#fff",
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 14px 32px rgba(30,64,175,.12)",
    border: "1px solid rgba(37,99,235,.10)",
  },
  imageBox: {
    height: 190,
    background: "#dbeafe",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  imageEmpty: {
    height: "100%",
    display: "grid",
    placeItems: "center",
    fontSize: 50,
  },
  cardBody: {
    padding: 16,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: 20,
    fontWeight: 900,
    color: "#111827",
    lineHeight: 1.3,
  },
  cardDesc: {
    margin: "0 0 12px",
    color: "#6b7280",
    fontSize: 14,
    lineHeight: 1.6,
    minHeight: 0,
  },
  meta: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 14,
    color: "#374151",
    marginBottom: 14,
    lineHeight: 1.5,
  },
  appointmentBtn: {
    width: "100%",
    border: "none",
    borderRadius: 999,
    padding: "12px 14px",
    background: "linear-gradient(135deg,#f97316,#2563eb)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  faqBox: {
  background: "linear-gradient(135deg,#ffffff,#fff7ed)",
  borderRadius: 28,
  padding: 20,
  boxShadow: "0 18px 40px rgba(30,64,175,.14)",
  border: "1px solid rgba(249,115,22,.18)",
},
faqItem: {
  border: "1px solid #dbeafe",
  borderRadius: 18,
  padding: "14px 16px",
  marginBottom: 12,
  background: "#ffffff",
  boxShadow: "0 10px 22px rgba(30,64,175,.08)",
  transition: "all .25s ease",
},
faqQuestion: {
  cursor: "pointer",
  fontWeight: 900,
  color: "#1e3a8a",
  lineHeight: 1.4,
  listStyle: "none",
},
faqAnswer: {
  color: "#6b7280",
  lineHeight: 1.7,
  marginTop: 12,
  animation: "faqFade .25s ease",
},


  empty: {
    background: "#fff",
    borderRadius: 24,
    padding: 40,
    textAlign: "center",
    color: "#9ca3af",
    fontWeight: 800,
  },
  footer: {
    background: "linear-gradient(135deg,#0f172a 0%,#1e3a8a 50%,#2563eb 100%)",
    padding: "54px 18px 38px",
    color: "#fff",
    marginTop: 48,
  },
  footerGrid: {
    maxWidth: 1180,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 34,
  },
  footerTitle: {
    margin: "0 0 16px",
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1.3,
  },
  footerText: {
    color: "#dbeafe",
    lineHeight: 1.7,
    margin: 0,
  },
  footerLine: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    color: "#dbeafe",
    margin: "0 0 12px",
    lineHeight: 1.6,
    wordBreak: "break-word",
  },
  footerLink: {
    color: "#ffffff",
    textDecoration: "none",
    wordBreak: "break-word",
  },
  socialLinks: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  socialBtn: {
    background: "#ffffff",
    color: "#1e3a8a",
    padding: "9px 14px",
    borderRadius: 999,
    fontWeight: 900,
    textDecoration: "none",
  },
  whatsappBtn: {
    padding: "9px 14px",
    borderRadius: 999,
    background: "#22c55e",
    color: "#fff",
    fontWeight: 900,
    textDecoration: "none",
  },
  mapBtn: {
    padding: "9px 14px",
    borderRadius: 999,
    background: "#f97316",
    color: "#fff",
    fontWeight: 900,
    textDecoration: "none",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.65)",
    zIndex: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    width: "100%",
    maxWidth: 460,
    background: "#fff",
    borderRadius: 26,
    padding: 22,
    position: "relative",
    boxShadow: "0 25px 70px rgba(0,0,0,.35)",
  },
  closeBtn: {
    position: "absolute",
    top: 12,
    right: 14,
    border: "none",
    background: "#f3f4f6",
    borderRadius: "50%",
    width: 34,
    height: 34,
    fontSize: 22,
    cursor: "pointer",
  },
  modalTitle: {
    marginTop: 8,
    color: "#1e3a8a",
    fontWeight: 900,
  },
  selectedService: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 16,
    padding: 12,
    marginBottom: 12,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },
  input: {
    width: "100%",
    padding: "13px 14px",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    fontSize: 15,
    marginBottom: 12,
    boxSizing: "border-box",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  sendBtn: {
    width: "100%",
    padding: 14,
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg,#f97316,#2563eb)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
reviewBox: {
  background: "#fff",
  borderRadius: 26,
  padding: 24,
  marginBottom: 34,
  textAlign: "center",
  boxShadow: "0 14px 34px rgba(30,64,175,.12)",
  border: "1px solid #dbeafe",
},

reviewIcon: {
  fontSize: 42,
  marginBottom: 8,
},

reviewTitle: {
  margin: 0,
  fontSize: 24,
  fontWeight: 900,
  color: "#1e3a8a",
},

reviewText: {
  color: "#6b7280",
  lineHeight: 1.6,
  marginTop: 10,
},

reviewBtn: {
  display: "inline-block",
  marginTop: 14,
  padding: "12px 24px",
  borderRadius: 999,
  background: "linear-gradient(135deg,#f97316,#2563eb)",
  color: "#fff",
  textDecoration: "none",
  fontWeight: 900,
},

  toast: {
    position: "fixed",
    bottom: 28,
    left: "50%",
    transform: "translateX(-50%)",
    background: "#16a34a",
    color: "#fff",
    padding: "12px 22px",
    borderRadius: 999,
    fontWeight: 800,
    zIndex: 1200,
    boxShadow: "0 12px 30px rgba(0,0,0,.25)",
  },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { 
      transform: rotate(360deg); 
    }
  }

  @keyframes faqFade {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  details[open] {
    transform: translateY(-2px);
  }

  details summary::-webkit-details-marker {
    display: none;
  }


  @media (max-width: 768px) {
    body {
      overflow-x: hidden;
    }

    .horizontal {
      scroll-snap-type: x mandatory;
    }

    .card {
      scroll-snap-align: start;
    }
  }
`;

if (typeof document !== "undefined" && !document.getElementById("beaty-menu-style")) {
  styleSheet.id = "beaty-menu-style";
  document.head.appendChild(styleSheet);
}